import React, { useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import Box from '@/components/Box';
import cx from '@/utils/cx';
import useStyle from './style';
import Swipe from '../../utils/swipe';

const EXTRA_DRAG_LENGTH = 20;
const COLLAPSE_CHANGE_THRESHOLD = 20;
const SHRINK_SCALE_RANGE = 0.04;

const DoubleStackingBoxes = ({
  className,
  collapsed: collapsedProp,
  onCollapse,
  onExpand,
  swipeEvents,
  children,
  ...rest
}) => {
  const theme = useTheme();
  const rootRef = useRef();
  const transitions = useRef({});
  const topBoxRef = useRef();
  const bottomBoxRef = useRef();
  const bottomBoxInitialHeightRef = useRef();
  const innerBottomBoxRef = useRef();
  const eventsAreOn = useRef(false);
  const collapsed = useRef(collapsedProp);
  const swipe = useRef(Swipe());
  const classes = useStyle();

  const calculateTopBoxBottom = () => {
    if (collapsed.current) {
      return theme.sizes.base;
    }
    if (bottomBoxRef.current) {
      return bottomBoxRef.current.offsetHeight + theme.sizes.base;
    }
    return 0;
  };

  const easeOutQuadContinuous = (x, upperLimit) => {
    if (x >= upperLimit) {
      return 1 + ((x - upperLimit) / 20);
    }
    return 1 - (1 - x) * (1 - x);
  };

  const calculateShrinkFrameStyle = (bottom, y) => {
    const maxBottom = bottomBoxInitialHeightRef.current;
    const minBottom = theme.sizes.base;
    const pureBottom = bottom - minBottom;
    const multiplier = pureBottom / maxBottom;

    let bottomBoxHeight;
    if (collapsed.current) {
      bottomBoxHeight = y > 0
        ? theme.sizes.base * 2
        : Math.min(theme.sizes.base - y, bottomBoxInitialHeightRef.current);
    } else {
      bottomBoxHeight = y > 0
        ? Math.max(theme.sizes.base * 2, bottomBoxInitialHeightRef.current - y)
        : bottomBoxInitialHeightRef.current;
    }
    return {
      bottomBoxTransform: `scale(${(1 - SHRINK_SCALE_RANGE) + (SHRINK_SCALE_RANGE * multiplier)})`,
      bottomBoxHeight: `${bottomBoxHeight}px`,
      innerBoxOpacity: Math.min(1, multiplier),
      rootHeight: `${bottom + topBoxRef.current.offsetHeight}px`,
    };
  };

  const applyFrameStyles = (frameStyles) => {
    bottomBoxRef.current.style.transform = frameStyles.bottomBoxTransform;
    bottomBoxRef.current.style.height = frameStyles.bottomBoxHeight;
    innerBottomBoxRef.current.style.opacity = frameStyles.innerBoxOpacity;
    rootRef.current.style.height = frameStyles.rootHeight;
  };

  const pauseTransitions = () => {
    transitions.current = {
      root: topBoxRef.current.style.transition,
      bottomBox: bottomBoxRef.current.style.transition,
      topBox: topBoxRef.current.style.transition,
    };
    rootRef.current.style.transition = 'none';
    bottomBoxRef.current.style.transition = 'none';
    topBoxRef.current.style.transition = 'none';
  };

  const resumeTransitions = () => {
    rootRef.current.style.transition = transitions.current.root;
    bottomBoxRef.current.style.transition = transitions.current.bottomBox;
    topBoxRef.current.style.transition = transitions.current.topBox;
  };

  const handleWhileSwipe = (pos, diff) => {
    if (!topBoxRef.current) {
      return;
    }
    const currentBottom = calculateTopBoxBottom();
    let finalBottom;

    if (diff.y < 0) {
      const yMovement = -diff.y;
      const freeMoveLength = collapsed.current
        ? bottomBoxInitialHeightRef.current + EXTRA_DRAG_LENGTH
        : EXTRA_DRAG_LENGTH;
      const easedYMovement = easeOutQuadContinuous(
        yMovement / freeMoveLength,
        1,
      ) * freeMoveLength;
      finalBottom = currentBottom + easedYMovement;
      topBoxRef.current.style.bottom = `${finalBottom}px`;
    } else {
      const easedYMovement = -diff.y;
      finalBottom = Math.max(theme.sizes.base, currentBottom + easedYMovement);
      topBoxRef.current.style.bottom = `${finalBottom}px`;
    }
    const frameStyles = calculateShrinkFrameStyle(finalBottom, diff.y);
    applyFrameStyles(frameStyles);
  };

  swipe.current.whileSwipe((pos, diff) => {
    requestAnimationFrame(() => {
      handleWhileSwipe(pos, diff);
    });
  });

  swipe.current.beforeSwipe(() => {
    pauseTransitions();
  });

  const collapseBoxes = () => {
    topBoxRef.current.style.bottom = `${theme.sizes.base}px`;
    applyFrameStyles({
      bottomBoxTransform: `scale(${1 - SHRINK_SCALE_RANGE})`,
      bottomBoxHeight: `${theme.sizes.base * 2}px`,
      innerBoxOpacity: 0,
      rootHeight: `${topBoxRef.current.offsetHeight + theme.sizes.base}px`,
    });
    if (collapsed.current === false) {
      onCollapse();
    }
    collapsed.current = true;
  };

  const expandBoxes = () => {
    topBoxRef.current.style.bottom = `${bottomBoxInitialHeightRef.current + theme.sizes.base}px`;
    applyFrameStyles({
      bottomBoxTransform: 'scale(1)',
      bottomBoxHeight: `${bottomBoxInitialHeightRef.current}px`,
      innerBoxOpacity: 1,
      rootHeight: `${topBoxRef.current.offsetHeight + bottomBoxInitialHeightRef.current + theme.sizes.base}px`,
    });
    if (collapsed.current === true) {
      onExpand();
    }
    collapsed.current = false;
  };

  const handleAfterSwipe = (pos, diff) => {
    resumeTransitions();
    if (Math.abs(diff.y) < COLLAPSE_CHANGE_THRESHOLD) {
      if (collapsed.current) {
        collapseBoxes();
      } else {
        expandBoxes();
      }
      return;
    }
    if (diff.y < 0) {
      expandBoxes();
    } else {
      collapseBoxes();
    }
  };

  const rootElementIsMounted = () => Boolean(rootRef.current);

  swipe.current.afterSwipe((pos, diff) => {
    requestAnimationFrame(() => {
      if (rootElementIsMounted()) {
        handleAfterSwipe(pos, diff);
      }
    });
  });

  useEffect(() => {
    if (collapsedProp) {
      onCollapse();
    } else {
      onExpand();
    }
  }, []);

  useLayoutEffect(() => {
    bottomBoxInitialHeightRef.current = innerBottomBoxRef.current.children[0].offsetHeight;
    if (collapsedProp) {
      collapseBoxes();
    } else {
      expandBoxes();
    }

    if (!eventsAreOn.current && swipeEvents === 'on') {
      eventsAreOn.current = true;
      swipe.current.setElement(rootRef.current);
      swipe.current.on();
    }
  }, [children, collapsedProp]);

  useEffect(() => {
    if (swipeEvents === 'on' && !eventsAreOn.current) {
      eventsAreOn.current = true;
      swipe.current.on();
    } else if (swipeEvents === 'off' && eventsAreOn.current) {
      eventsAreOn.current = false;
      swipe.current.off();
    }
  }, [swipeEvents]);

  return (
    <div
      className={cx(
        classes.boxStack,
        className,
      )}
      ref={rootRef}
      {...rest}
    >
      <Box
        className={cx(
          classes.child,
        )}
        _color="background"
        _border="md"
        _shadow="md"
        _radius="md"
        style={{
          bottom: 0,
        }}
        ref={bottomBoxRef}
      >
        <div
          className={classes.bottomBoxInner}
          ref={innerBottomBoxRef}
        >
          {children[0]}
        </div>
      </Box>
      <Box
        className={classes.child}
        _color="background"
        _border="md"
        _shadow="md"
        _radius="md"
        style={{
          bottom: `${calculateTopBoxBottom()}px`,
        }}
        ref={topBoxRef}
      >
        {children[1]}
      </Box>
    </div>
  );
};

DoubleStackingBoxes.propTypes = {
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
  onExpand: PropTypes.func,
  swipeEvents: PropTypes.oneOf(['on', 'off']),
};

DoubleStackingBoxes.defaultProps = {
  className: '',
  collapsed: true,
  onCollapse: () => { },
  onExpand: () => { },
  swipeEvents: 'on',
};

export default DoubleStackingBoxes;
